<template>
  <div>
    <BaseReport
      ref="baseReport"
      :loading="loading"
      :reportTitle="reportTitle"
      :reportSubtitle="dateLabel"
      :columns="5"
      @mounted="loadData"
    >
      <template #controls>
        <div class="report-controls">
          <button class="nav-btn" @click="goToPrevDay" title="Previous day">&larr;</button>
          <input type="date" v-model="selectedDate" @change="handleDateChange" class="date-input" />
          <button class="nav-btn" @click="goToNextDay" title="Next day">&rarr;</button>
        </div>
      </template>

      <template #header>
        <h2>Daily Sales Sheet</h2>
        <p>All payments received on this day — includes new ticket payments and payment plan installments</p>
      </template>

      <template #content>
      <CardKpi
        class="cell cell-kpi-1"
        title="New Patient Payments"
        :value="newPatientPaymentsKpi.value"
        format="currency"
        :description="newPatientPaymentsKpi.description"
      />
      <CardKpi
        class="cell cell-kpi-2"
        title="Current Patient Payments"
        :value="currentPatientPaymentsKpi.value"
        format="currency"
        :description="currentPatientPaymentsKpi.description"
      />
      <CardKpi
        class="cell cell-kpi-3"
        title="Initial Payments"
        :value="initialPaymentsKpi.value"
        format="currency"
        :description="initialPaymentsKpi.description"
      />
      <CardKpi
        class="cell cell-kpi-4"
        title="Payment Plan Payments"
        :value="paymentPlanPaymentsKpi.value"
        format="currency"
        :description="paymentPlanPaymentsKpi.description"
      />
      <CardKpi
        class="cell cell-kpi-5"
        title="Total Payments"
        :value="totalPaymentsKpi.value"
        format="currency"
        :description="totalPaymentsKpi.description"
      />

      <CardTable
        class="cell cell-tickets-table"
        title="Daily Sales"
        subtitle="Click row to view ticket"
        :columns="ticketColumns"
        :rows="ticketRows"
        variant="summary"
        :summaryRow="summaryRow"
        :maxRows="100"
        :clickable="true"
        :stickyFirstColumn="false"
        @row-click="handleRowClick"
      />
      </template>
    </BaseReport>
  </div>
</template>

<script>
import BaseReport from '@/components/common/BaseReport.vue';
import CardTable from '@/components/common/CardTable.vue';
import CardKpi from '@/components/common/CardKpi.vue';
import * as api from '@/api';
import { getLocalToday, addDays } from '@/utils/dateUtils';
import { formatDateLong } from '@/utils/formatters';
import { jsPDF } from 'jspdf';

export default {
  name: 'DailyReport',
  components: {
    BaseReport,
    CardTable,
    CardKpi
  },
  emits: ['loading'],
  data() {
    return {
      reportTitle: 'Daily Sales Sheet',
      loading: true,
      selectedDate: this.$route?.query?.date || getLocalToday(),

      ticketRows: [],

      ticketColumns: [
        { key: 'ticket', label: 'Ticket #' },
        { key: 'patient', label: 'Patient Name' },
        { key: 'counselor', label: 'Counselor' },
        { key: 'products', label: 'Products' },
        { key: 'ticketType', label: 'Ticket Type' },
        { key: 'paymentType', label: 'Payment Type' },
        { key: 'paymentMethod', label: 'Payment Method' },
        { key: 'amountPaidToday', label: 'Amount Paid', format: 'currency' },
        { key: 'amountRefundedToday', label: 'Amount Refunded', format: 'currency' },
        { key: 'ticketTotalAmount', label: 'Ticket Total', format: 'currency' },
        { key: 'ticketBalance', label: 'Balance', format: 'currency' }
      ]
    };
  },
  computed: {
    dateLabel() {
      if (!this.selectedDate) return '';
      const todayStr = getLocalToday();
      const formatted = formatDateLong(this.selectedDate);

      if (this.selectedDate === todayStr) {
        return `Today - ${formatted}`;
      }
      return formatted;
    },

    summaryRow() {
      if (this.ticketRows.length === 0) return null;

      const totals = this.ticketRows.reduce((acc, row) => {
        acc.amountPaidToday += row.amountPaidToday || 0;
        acc.amountRefundedToday += row.amountRefundedToday || 0;
        acc.ticketTotalAmount += row.ticketTotalAmount || 0;
        acc.ticketBalance += row.ticketBalance || 0;
        return acc;
      }, {
        amountPaidToday: 0,
        amountRefundedToday: 0,
        ticketTotalAmount: 0,
        ticketBalance: 0
      });

      const ticketCount = this.ticketRows.length;
      const uniquePatients = new Set(this.ticketRows.map(r => r.patient)).size;
      const uniqueCounselors = new Set(this.ticketRows.map(r => r.counselor)).size;
      const uniqueProducts = new Set(this.ticketRows.flatMap(r => r.productNames)).size;
      const newCount = this.ticketRows.filter(r => r.ticketType === 'New Patient').length;
      const currentCount = this.ticketRows.filter(r => r.ticketType === 'Current Patient').length;
      const initialCount = this.ticketRows.filter(r => r.paymentType === 'Initial Payment').length;
      const planCount = this.ticketRows.filter(r => r.paymentType === 'Plan Payment').length;
      const uniqueMethods = new Set(this.ticketRows.map(r => r.paymentMethod).filter(m => m && m !== '—'));
      const methodCount = uniqueMethods.size;

      return {
        ticket: `${ticketCount} tickets`,
        patient: `${uniquePatients} patients`,
        counselor: `${uniqueCounselors} counselors`,
        products: `${uniqueProducts} products`,
        ticketType: `${newCount} NP / ${currentCount} EP`,
        paymentType: `${initialCount} Init / ${planCount} Plan`,
        paymentMethod: methodCount > 0 ? `${methodCount} method${methodCount !== 1 ? 's' : ''}` : '—',
        amountPaidToday: totals.amountPaidToday,
        amountRefundedToday: totals.amountRefundedToday,
        ticketTotalAmount: totals.ticketTotalAmount,
        ticketBalance: totals.ticketBalance
      };
    },

    newPatientPaymentsKpi() {
      const rows = this.ticketRows.filter(row => row.ticketType === 'New Patient');
      return this.buildKpi(rows, false);
    },

    currentPatientPaymentsKpi() {
      const rows = this.ticketRows.filter(row => row.ticketType === 'Current Patient');
      return this.buildKpi(rows, false);
    },

    initialPaymentsKpi() {
      const rows = this.ticketRows.filter(row => row.paymentType === 'Initial Payment');
      return this.buildKpi(rows, false);
    },

    paymentPlanPaymentsKpi() {
      const rows = this.ticketRows.filter(row => row.paymentType === 'Plan Payment');
      return this.buildKpi(rows, false);
    },

    totalPaymentsKpi() {
      const rows = this.ticketRows.filter(row => row.paymentType === 'Initial Payment' || row.paymentType === 'Plan Payment');
      return this.buildKpi(rows, true);
    },

    paymentMethodTotals() {
      const totals = {};
      this.ticketRows.forEach(row => {
        const method = row.paymentMethod || '—';
        if (method === '—') return;
        if (!totals[method]) totals[method] = 0;
        totals[method] += row.amountPaidToday || 0;
      });
      return Object.entries(totals)
        .sort((a, b) => b[1] - a[1])
        .map(([method, amount]) => ({ method, amount }));
    }
  },
  methods: {
    buildKpi(rows, showNet) {
      const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
      const paid = rows.reduce((sum, row) => sum + (row.amountPaidToday || 0), 0);
      const count = rows.length;
      let description = `Across ${count} ticket${count !== 1 ? 's' : ''}`;
      if (showNet) {
        const refunded = rows.reduce((sum, row) => sum + (row.amountRefundedToday || 0), 0);
        description += `. Net after refunds: ${fmt(paid - refunded)}`;
      }
      return { value: paid, description };
    },

    formatPaymentMethod(value) {
      switch (value) {
        case 'cash': return 'Cash';
        case 'check': return 'Check';
        case 'mastercard': return 'Mastercard';
        case 'visa': return 'Visa';
        case 'amex': return 'AMX';
        case 'discover': return 'Discover';
        case 'cashiers_check': return 'Cashiers Check';
        case 'agms': return 'AGMS';
        case 'hsa': return 'HSA';
        case 'ach': return 'ACH';
        case 'finance': return 'Finance';
        default: return value || '—';
      }
    },

    handleDateChange() {
      this.loading = true;
      this.$emit('loading', true);
      this.syncDateToUrl();
      this.loadData();
    },

    syncDateToUrl() {
      const query = { ...this.$route.query };
      if (this.selectedDate && this.selectedDate !== getLocalToday()) {
        query.date = this.selectedDate;
      } else {
        delete query.date;
      }
      this.$router.replace({ query });
    },

    goToPrevDay() {
      this.selectedDate = addDays(this.selectedDate, -1);
      this.handleDateChange();
    },

    goToNextDay() {
      this.selectedDate = addDays(this.selectedDate, 1);
      this.handleDateChange();
    },

    async loadData() {
      if (!this.selectedDate) return;

      try {
        const data = await api.fetchDailySalesTickets({ date: this.selectedDate });

        this.ticketRows = data.tickets.map(t => ({
          ticketId: t.ticketId,
          ticket: String(t.ticketId),
          patient: `${t.patientFirstName} ${t.patientLastName}`,
          counselor: t.counselorName,
          productNames: t.products?.map(p => p.name) || [],
          products: t.products?.map(p => `${p.name} (x${p.quantity})`).join(', ') || '—',
          ticketType: t.ticketType,
          paymentType: t.paymentType === 'Initial Payment' ? 'Initial Payment' : 'Plan Payment',
          paymentMethod: this.formatPaymentMethod(t.paymentMethod),
          amountPaidToday: t.amountPaidTodayCents / 100,
          amountRefundedToday: t.amountRefundedTodayCents / 100,
          ticketTotalAmount: t.ticketTotalAmountCents / 100,
          ticketBalance: t.ticketBalanceCents / 100
        }));
      } catch (error) {
        console.error('Error loading daily sales tickets:', error);
        this.ticketRows = [];
      }

      this.loading = false;
      this.$emit('loading', false);
    },

    async generatePDF() {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;

      // Load Rubik font from CDN
      let fontFamily = 'helvetica';
      try {
        const fontUrl = 'https://cdn.jsdelivr.net/fontsource/fonts/rubik@latest/latin-400-normal.ttf';
        const fontResponse = await fetch(fontUrl);
        if (!fontResponse.ok) throw new Error('Failed to fetch regular font');
        const fontBuffer = await fontResponse.arrayBuffer();
        const fontBytes = new Uint8Array(fontBuffer);
        let fontBase64 = '';
        for (let i = 0; i < fontBytes.length; i++) {
          fontBase64 += String.fromCharCode(fontBytes[i]);
        }
        fontBase64 = btoa(fontBase64);
        doc.addFileToVFS('Rubik-Regular.ttf', fontBase64);
        doc.addFont('Rubik-Regular.ttf', 'Rubik', 'normal');

        const fontBoldUrl = 'https://cdn.jsdelivr.net/fontsource/fonts/rubik@latest/latin-700-normal.ttf';
        const fontBoldResponse = await fetch(fontBoldUrl);
        if (!fontBoldResponse.ok) throw new Error('Failed to fetch bold font');
        const fontBoldBuffer = await fontBoldResponse.arrayBuffer();
        const fontBoldBytes = new Uint8Array(fontBoldBuffer);
        let fontBoldBase64 = '';
        for (let i = 0; i < fontBoldBytes.length; i++) {
          fontBoldBase64 += String.fromCharCode(fontBoldBytes[i]);
        }
        fontBoldBase64 = btoa(fontBoldBase64);
        doc.addFileToVFS('Rubik-Bold.ttf', fontBoldBase64);
        doc.addFont('Rubik-Bold.ttf', 'Rubik', 'bold');

        fontFamily = 'Rubik';
      } catch (fontErr) {
        console.warn('Failed to load Rubik font, falling back to helvetica:', fontErr);
        fontFamily = 'helvetica';
      }

      doc.setFillColor(248, 248, 248);
      doc.rect(0, 0, pageWidth, 60, 'F');
      doc.setFont(fontFamily, 'bold');
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.text('Daily Sales Sheet', margin, 30);
      doc.setFont(fontFamily, 'normal');
      doc.setFontSize(12);
      doc.setTextColor(80, 80, 80);
      doc.text(this.dateLabel, margin, 48);

      let y = 80;

      const kpiData = [
        { label: 'New Patient Payments', value: this.newPatientPaymentsKpi.value, description: this.newPatientPaymentsKpi.description },
        { label: 'Current Patient Payments', value: this.currentPatientPaymentsKpi.value, description: this.currentPatientPaymentsKpi.description },
        { label: 'Initial Payments', value: this.initialPaymentsKpi.value, description: this.initialPaymentsKpi.description },
        { label: 'Payment Plan Payments', value: this.paymentPlanPaymentsKpi.value, description: this.paymentPlanPaymentsKpi.description },
        { label: 'Total Payments', value: this.totalPaymentsKpi.value, description: this.totalPaymentsKpi.description }
      ];

      const kpiHeight = 62;
      const kpiWidth = (pageWidth - margin * 2 - 40) / 5;
      kpiData.forEach((kpi, index) => {
        const x = margin + index * (kpiWidth + 10);
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(1);
        doc.rect(x, y, kpiWidth, kpiHeight, 'S');
        doc.setFont(fontFamily, 'normal');
        doc.setFontSize(8);
        doc.setTextColor(60, 60, 60);
        doc.text(kpi.label, x + 10, y + 15);
        doc.setFont(fontFamily, 'bold');
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        const formattedValue = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2
        }).format(kpi.value);
        doc.text(formattedValue, x + 10, y + 38);
        if (kpi.description) {
          doc.setFont(fontFamily, 'normal');
          doc.setFontSize(5.5);
          doc.setTextColor(100, 100, 100);
          doc.text(kpi.description, x + 10, y + 50, { maxWidth: kpiWidth - 20 });
        }
      });

      y += kpiHeight + 16;

      const tableWidth = pageWidth - margin * 2;
      const numCols = 11;
      const colWidth = tableWidth / numCols;
      const colWidths = Array(numCols).fill(colWidth);
      const rowHeight = 16;

      const headers = [
        'Ticket #',
        'Patient Name',
        'Counselor',
        'Products',
        'Ticket Type',
        'Payment',
        'Method',
        'Paid',
        'Refunded',
        'Total',
        'Balance'
      ];

      doc.setFillColor(248, 248, 248);
      doc.rect(margin, y, tableWidth, rowHeight, 'F');
      doc.setFont(fontFamily, 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(0, 0, 0);

      let x = margin;
      headers.forEach((header, i) => {
        doc.text(header, x + 4, y + 10, { maxWidth: colWidths[i] - 8 });
        x += colWidths[i];
      });

      y += rowHeight;

      doc.setFont(fontFamily, 'normal');
      doc.setFontSize(6);
      doc.setTextColor(0, 0, 0);

      // Helper to clip text with ellipsis to fit within maxWidth
      const clipText = (text, maxWidth) => {
        text = String(text);
        if (doc.getTextWidth(text) <= maxWidth) return text;
        let clipped = text;
        while (clipped.length > 0 && doc.getTextWidth(clipped + '...') > maxWidth) {
          clipped = clipped.slice(0, -1);
        }
        return clipped + '...';
      };

      this.ticketRows.forEach((row) => {
        const rowData = [
          row.ticket,
          row.patient,
          row.counselor,
          row.products,
          row.ticketType,
          row.paymentType,
          row.paymentMethod,
          new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.amountPaidToday),
          new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.amountRefundedToday),
          new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.ticketTotalAmount),
          new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.ticketBalance)
        ];

        if (y + rowHeight > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }

        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(margin, y + rowHeight, margin + tableWidth, y + rowHeight);

        x = margin;
        rowData.forEach((cell, i) => {
          const cellMaxWidth = colWidths[i] - 8;
          const text = clipText(cell, cellMaxWidth);
          doc.text(text, x + 4, y + 10);
          x += colWidths[i];
        });

        y += rowHeight;
      });

      if (this.summaryRow) {
        const summaryData = [
          this.summaryRow.ticket,
          this.summaryRow.patient,
          this.summaryRow.counselor,
          this.summaryRow.products,
          this.summaryRow.ticketType,
          this.summaryRow.paymentType,
          this.summaryRow.paymentMethod,
          new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.summaryRow.amountPaidToday),
          new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.summaryRow.amountRefundedToday),
          new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.summaryRow.ticketTotalAmount),
          new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.summaryRow.ticketBalance)
        ];

        if (y + rowHeight > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }

        doc.setFillColor(240, 240, 240);
        doc.rect(margin, y, tableWidth, rowHeight, 'F');
        doc.setFont(fontFamily, 'bold');

        x = margin;
        summaryData.forEach((cell, i) => {
          const cellMaxWidth = colWidths[i] - 8;
          const text = clipText(cell, cellMaxWidth);
          doc.text(text, x + 4, y + 10);
          x += colWidths[i];
        });
      }

      // Payment method totals - subtle line at the bottom of the last page
      if (this.paymentMethodTotals.length > 0) {
        const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v);
        const parts = this.paymentMethodTotals.map(t => `${t.method}: ${fmt(t.amount)}`);
        const line = `Totals by method  \u2014  ${parts.join('   |   ')}`;
        doc.setFont(fontFamily, 'normal');
        doc.setFontSize(6);
        doc.setTextColor(140, 140, 140);
        doc.text(line, margin, pageHeight - 20);
      }

      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
    },

    handleRowClick(row) {
      if (row.ticketId) {
        this.$router.push(`/tickets/${row.ticketId}`);
      }
    }
  }
};
</script>

<style scoped>
/* Report controls layout */
.report-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  background: #fff;
  color: #374151;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.15s;
}

.nav-btn:hover {
  border-color: #9ca3af;
  background: #f9fafb;
}

.nav-btn:active {
  background: #f3f4f6;
}

.date-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  font-size: 14px;
  color: #262626;
  background: #fff;
  cursor: pointer;
}

.date-input:hover {
  border-color: #9ca3af;
}

.date-input:focus {
  outline: none;
  border-color: #4338ca;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.1);
}

/* Grid cell positioning */
.cell {
  background: #fff;
}

.cell-kpi-1 {
  grid-column: 1;
  grid-row: 1;
}

.cell-kpi-2 {
  grid-column: 2;
  grid-row: 1;
}

.cell-kpi-3 {
  grid-column: 3;
  grid-row: 1;
}

.cell-kpi-4 {
  grid-column: 4;
  grid-row: 1;
}

.cell-kpi-5 {
  grid-column: 5;
  grid-row: 1;
}

.cell-tickets-table {
  grid-column: 1 / -1;
  grid-row: 2;
}

/* Scrollable table with fixed-width columns */
.cell-tickets-table :deep(.card-body) {
  overflow-x: auto;
}

.cell-tickets-table :deep(.data-table) {
  min-width: max-content;
}

.cell-tickets-table :deep(.data-table th),
.cell-tickets-table :deep(.data-table td) {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
}
</style>
